/**
 * UIPageEditor — authenticated editor surface for a UI Page.
 *
 * Shows the live preview (rendered through UiPageRenderer) plus controls to:
 *  - Re-open the UI Planner modal to refine the page
 *  - Rename the page
 *  - Publish / unpublish (toggle public access at /p/<slug>)
 *  - Copy the public URL
 *  - Delete the page
 */
const React = require('react');
const { createElement: h, useState, useEffect, useCallback } = React;
const uiPageStorage = require('../lib/uiPageStorage');
const UiPageRenderer = require('./UiPageRenderer');
const ConfirmDialog = require('./ConfirmDialog');

function UIPageEditor(props) {
  var pageId = props.pageId;
  var onClose = props.onClose || function() {};
  var onOpenPlanner = props.onOpenPlanner || function() {};
  var onDeleted = props.onDeleted || function() {};

  var [page, setPage] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [saving, setSaving] = useState(false);
  var [editingName, setEditingName] = useState(false);
  var [nameDraft, setNameDraft] = useState('');
  var [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  var refresh = useCallback(function() {
    if (!pageId) return;
    setLoading(true);
    uiPageStorage.loadUiPage(pageId).then(function(p) {
      setPage(p);
      setLoading(false);
      if (p) setNameDraft(p.name || '');
    }).catch(function(err) {
      setError(err && err.message ? err.message : 'Failed to load page');
      setLoading(false);
    });
  }, [pageId]);

  useEffect(function() { refresh(); }, [refresh]);

  function handleRename() {
    if (!page) return;
    var newName = String(nameDraft || '').trim() || 'Untitled page';
    setSaving(true);
    uiPageStorage.updateUiPage(pageId, { name: newName }).then(function(updated) {
      setPage(updated);
      setSaving(false);
      setEditingName(false);
    }).catch(function(err) {
      setSaving(false);
      setError(err && err.message ? err.message : 'Rename failed');
    });
  }

  function handlePublish() {
    setSaving(true);
    uiPageStorage.publishUiPage(pageId).then(function(updated) {
      setPage(updated);
      setSaving(false);
    }).catch(function(err) {
      setSaving(false);
      setError(err && err.message ? err.message : 'Publish failed');
    });
  }

  function handleUnpublish() {
    setSaving(true);
    uiPageStorage.unpublishUiPage(pageId).then(function(updated) {
      setPage(updated);
      setSaving(false);
    }).catch(function(err) {
      setSaving(false);
      setError(err && err.message ? err.message : 'Unpublish failed');
    });
  }

  function handleCopyPublicUrl() {
    if (!page || !page.publicSlug) return;
    var url = window.location.origin + '/p/' + page.publicSlug;
    if (navigator.clipboard) navigator.clipboard.writeText(url);
  }

  function handleDelete() {
    if (!page) return;
    setConfirmDeleteOpen(true);
  }

  function performDelete() {
    uiPageStorage.deleteUiPage(pageId).then(function() {
      onDeleted(pageId);
    }).catch(function(err) {
      setError(err && err.message ? err.message : 'Delete failed');
    });
  }

  if (loading) {
    return h('div', { style: { padding: 40, textAlign: 'center', color: '#64748b' } }, 'Loading...');
  }
  if (error && !page) {
    return h('div', { style: { padding: 40, textAlign: 'center', color: '#dc2626' } }, error);
  }
  if (!page) {
    return h('div', { style: { padding: 40, textAlign: 'center', color: '#64748b' } }, 'UI page not found.');
  }

  return h('div', {
    style: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: '#f1f5f9',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 100,
      minHeight: 0
    }
  },
    confirmDeleteOpen
      ? h(ConfirmDialog, {
          title: 'Delete UI page?',
          message: 'This cannot be undone.',
          confirmLabel: 'Delete',
          cancelLabel: 'Cancel',
          danger: true,
          onConfirm: function() {
            setConfirmDeleteOpen(false);
            performDelete();
          },
          onCancel: function() { setConfirmDeleteOpen(false); }
        })
      : null,
    // Top bar
    h('div', {
      style: {
        height: '56px',
        background: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        padding: '0 20px',
        gap: '16px'
      }
    },
      h('button', {
        onClick: onClose,
        style: {
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px',
          padding: '6px 10px', borderRadius: '6px', fontSize: '13px'
        }
      },
        h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
          h('polyline', { points: '15 18 9 12 15 6' })
        ),
        'Back'
      ),
      // Name (editable)
      editingName
        ? h('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', flex: 1 } },
            h('input', {
              type: 'text',
              value: nameDraft,
              onChange: function(e) { setNameDraft(e.target.value); },
              onKeyDown: function(e) {
                if (e.key === 'Enter') handleRename();
                if (e.key === 'Escape') { setEditingName(false); setNameDraft(page.name || ''); }
              },
              autoFocus: true,
              style: {
                fontSize: '15px', fontWeight: 600, padding: '6px 10px',
                border: '1px solid #cbd5e1', borderRadius: '6px', minWidth: '240px'
              }
            }),
            h('button', {
              onClick: handleRename,
              disabled: saving,
              style: { padding: '6px 12px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }
            }, 'Save')
          )
        : h('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', flex: 1 } },
            h('div', { style: { fontSize: '15px', fontWeight: 600, color: '#0f172a' } }, page.name),
            h('button', {
              onClick: function() { setEditingName(true); },
              style: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px' },
              title: 'Rename'
            },
              h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
                h('path', { d: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7' }),
                h('path', { d: 'M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z' })
              )
            )
          ),
      // Actions
      h('button', {
        onClick: function() { onOpenPlanner(page); },
        style: {
          padding: '8px 14px', background: '#0f172a', color: '#fff',
          border: 'none', borderRadius: '6px', cursor: 'pointer',
          fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px'
        }
      },
        h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
          h('path', { d: 'M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z' })
        ),
        'Open Planner'
      ),
      page.isPublic
        ? h('button', {
            onClick: handleUnpublish,
            disabled: saving,
            style: {
              padding: '8px 14px', background: '#fff', color: '#dc2626',
              border: '1px solid #fca5a5', borderRadius: '6px', cursor: 'pointer',
              fontSize: '13px', fontWeight: 500
            }
          }, 'Unpublish')
        : h('button', {
            onClick: handlePublish,
            disabled: saving,
            style: {
              padding: '8px 14px', background: '#22c55e', color: '#fff',
              border: 'none', borderRadius: '6px', cursor: 'pointer',
              fontSize: '13px', fontWeight: 500
            }
          }, 'Publish'),
      page.isPublic && page.publicSlug
        ? h('button', {
            onClick: handleCopyPublicUrl,
            style: {
              padding: '8px 14px', background: '#fff', color: '#475569',
              border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer',
              fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px'
            },
            title: 'Copy public URL'
          },
            h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
              h('rect', { x: 9, y: 9, width: 13, height: 13, rx: 2, ry: 2 }),
              h('path', { d: 'M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1' })
            ),
            '/p/' + page.publicSlug
          )
        : null,
      h('button', {
        onClick: handleDelete,
        style: {
          padding: '8px 12px', background: 'transparent', color: '#94a3b8',
          border: 'none', cursor: 'pointer', fontSize: '13px'
        },
        title: 'Delete'
      },
        h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
          h('polyline', { points: '3 6 5 6 21 6' }),
          h('path', { d: 'M19 6l-2 14a2 2 0 01-2 2H9a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2' })
        )
      )
    ),
    // Preview area — flex column so the inner card gets a definite height
    // and the iframe (height: 100%) actually fills it. Without minHeight: 0
    // on the parent, the iframe collapses to its srcdoc's intrinsic height.
    h('div', {
      style: {
        flex: 1,
        minHeight: 0,
        padding: '24px',
        display: 'flex',
        flexDirection: 'column'
      }
    },
      h('div', {
        style: {
          flex: 1,
          minHeight: 0,
          maxWidth: '1100px',
          width: '100%',
          margin: '0 auto',
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05), 0 1px 2px -1px rgba(0,0,0,0.05)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }
      },
        h(UiPageRenderer, { value: page.value })
      )
    ),
    error
      ? h('div', { style: { position: 'absolute', bottom: 20, right: 20, background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: '6px', fontSize: '13px' } }, error)
      : null
  );
}

module.exports = UIPageEditor;
